// src/utils/emailGateway.js

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Send status- eller quote-link e-post via backend-malen
 *
 * Backend-endepunkt:
 *   POST /email/workshop-status
 *
 * @param {object} params
 * @param {string} params.to            - Mottakerens e-postadresse
 * @param {string} params.customerName  - Navn som brukes i e-posten
 * @param {string} params.status        - Status (brukes når emailKind = "status")
 * @param {string} params.serviceRef    - Servicereferanse
 * @param {string} params.customerLink  - Lenke til kundeportalen (påkrevd)
 * @param {string} [params.plainStatusText] - Ekstra tekst / statusmelding
 * @param {string} [params.storeLocation]   - F.eks. "Sport 1 Røyken"
 * @param {"status"|"quote-link"} [params.emailKind="status"]
 *
 * @returns {Promise<{ok: boolean, status: number, body: any}>}
 */
export async function sendWorkshopStatusEmail({
  to,
  customerName,
  status,
  serviceRef,
  customerLink,
  plainStatusText,
  storeLocation,
  emailKind = "status",
}) {
  if (!to) {
    throw new Error("sendWorkshopStatusEmail: 'to' mangler");
  }
  if (!customerLink) {
    throw new Error("sendWorkshopStatusEmail: 'customerLink' mangler");
  }

  const payload = {
    to,
    customerName,
    status,
    serviceRef,
    customerLink,
    plainStatusText,
    storeLocation,
    emailKind,
  };

  const res = await fetch(`${API_BASE_URL}/email/workshop-status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { raw: text };
  }

  if (!res.ok) {
    const msg =
      body?.message ||
      `sendWorkshopStatusEmail: HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return { ok: true, status: res.status, body };
}

/**
 * Send pristilbud som PDF på e-post (brukes sammen med kundeportalen)
 *
 * Backend-endepunkt:
 *   POST /workshop-services/:id/send-quote-email
 *
 * @param {object} params
 * @param {string} params.svcId         - Service-ID i databasen
 * @param {string} params.to            - Mottakerens e-postadresse
 * @param {string} params.customerName  - Navn i e-posten
 * @param {string} [params.customerLink] - (valgfritt) lenke til kundeportalen
 *
 * @returns {Promise<{ok: boolean, status: number, body: any}>}
 */
export async function sendWorkshopQuoteEmail({
  svcId,
  to,
  customerName,
  customerLink,
}) {
  if (!svcId) {
    throw new Error("sendWorkshopQuoteEmail: 'svcId' mangler");
  }
  if (!to) {
    throw new Error("sendWorkshopQuoteEmail: 'to' mangler");
  }

  const url = `${API_BASE_URL}/workshop-services/${svcId}/send-quote-email`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      to,
      customerName,
      customerLink, // optional – backend kan ignorere/brukes hvis ønsket
    }),
  });

  const text = await res.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { raw: text };
  }

  if (!res.ok) {
    const msg =
      body?.message ||
      `sendWorkshopQuoteEmail: HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return { ok: true, status: res.status, body };
}
